/**
 * Pre-rasterizes the lobby's IconAvatar SVG into a Pixi Texture, then
 * registers the Texture under the icon's hash. drawCharacter's texture
 * fast-path then draws it pixel-identically to the lobby card.
 *
 * Pipeline:
 *   1. `renderToStaticMarkup(<IconAvatar icon={..} size={256} />)` — SSR
 *      produces deterministic HTML/SVG markup. Lazy-imported so
 *      `react-dom/server` doesn't bloat the entry chunk.
 *   2. DOMParser → extract the inner <svg> + its CSS radial-gradient
 *      backdrop. Wrap into a self-contained SVG document (viewBox 0..100,
 *      a `<radialGradient>` backdrop, a `<clipPath>` for the avatar
 *      circle, then the original inner SVG content).
 *   3. Encode as a `data:image/svg+xml` URL — same-origin under all
 *      browsers including mobile Safari (blob URLs occasionally trip up
 *      Safari's `<img>`-SVG path).
 *   4. Load via HTMLImageElement → drawImage onto a 2D canvas at 4× the
 *      logical 100×100 viewBox (400×400 raster) for crisp display under
 *      arbitrary in-game scaling.
 *   5. `Texture.from(canvas)` — wraps the canvas as a Pixi texture
 *      source. drawCharacter's texture fast-path renders this as a
 *      Sprite, sized to whatever each game asks for.
 *
 * Why not Pixi's native `Graphics.svg()`? It silently skips radial
 * gradients (`parseSVGDefinitions.js` has a hardcoded `warn(…not yet
 * supported)`), ignores transforms, and drops clip-paths — i.e. it
 * can't faithfully render the lobby's SVG. The browser's SVG renderer
 * supports all of those; using it as the rasterizer is the only path
 * that produces output matching the lobby card.
 */
import { Texture, type Application } from 'pixi.js';
import { createElement } from 'react';
import type { CharacterIcon } from '@tt/types';
import {
  iconHash,
  setCharacterTextureByIconHash,
  clearCharacterTextures,
} from '@tt/character-render';
import { IconAvatar } from '../characters/IconAvatar';
import type { IconValue } from '../characters/IconAvatar';

const SVG_NS = 'http://www.w3.org/2000/svg';
/** 4× the 100×100 viewBox = sharp at typical in-game sprite sizes (22–70 px). */
const RASTER_SIZE = 400;

interface ParsedAvatar {
  bgFill: string;
  bgFillDark: string;
  innerSvgXml: string;
}

let cachedRenderer: ((el: ReturnType<typeof createElement>) => string) | null = null;
async function getRenderer(): Promise<(el: ReturnType<typeof createElement>) => string> {
  if (cachedRenderer) return cachedRenderer;
  const mod = await import('react-dom/server');
  cachedRenderer = mod.renderToStaticMarkup;
  return cachedRenderer;
}

async function renderAndExtract(icon: IconValue, size: number): Promise<ParsedAvatar | null> {
  const renderToStaticMarkup = await getRenderer();
  const html = renderToStaticMarkup(createElement(IconAvatar, { icon, size }));
  if (!html) return null;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const span = doc.querySelector('span.icon-avatar') as HTMLElement | null;
  if (!span) {
    console.warn('[iconTexture] no .icon-avatar span in rendered markup', html.slice(0, 200));
    return null;
  }
  const svgEl = span.querySelector('svg');
  if (!svgEl) {
    console.warn('[iconTexture] no <svg> in IconAvatar output');
    return null;
  }

  const bgCss = span.getAttribute('style') || '';
  const m = bgCss.match(/(hsl\([^)]+\))\s*0%\s*,\s*(hsl\([^)]+\))\s*100%/);
  const bgFill = m?.[1] ?? 'hsl(20 40% 20%)';
  const bgFillDark = m?.[2] ?? 'hsl(20 40% 8%)';

  const serializer = new XMLSerializer();
  const parts: string[] = [];
  for (let i = 0; i < svgEl.childNodes.length; i++) {
    const node = svgEl.childNodes[i];
    if (!node) continue;
    parts.push(serializer.serializeToString(node));
  }
  return { bgFill, bgFillDark, innerSvgXml: parts.join('') };
}

function buildSvgDocument(parsed: ParsedAvatar): string {
  // Faithful reproduction of the lobby's avatar:
  //   span CSS radial-gradient backdrop → SVG <radialGradient>
  //   span border-radius:50% → SVG <clipPath> on a circle
  //   span's child svg's children → drawn inside the clip
  // The browser renders all of this perfectly; we never modify the
  // inner SVG markup, so all richSvg cosmetics show up identically.
  return `<svg xmlns="${SVG_NS}" viewBox="0 0 100 100" width="${RASTER_SIZE}" height="${RASTER_SIZE}">`
    + `<defs>`
    +   `<radialGradient id="ttBg" cx="35%" cy="30%" r="65%">`
    +     `<stop offset="0%" stop-color="${parsed.bgFill}"/>`
    +     `<stop offset="100%" stop-color="${parsed.bgFillDark}"/>`
    +   `</radialGradient>`
    +   `<clipPath id="ttClip"><circle cx="50" cy="50" r="50"/></clipPath>`
    + `</defs>`
    + `<g clip-path="url(#ttClip)">`
    +   `<circle cx="50" cy="50" r="50" fill="url(#ttBg)"/>`
    +   parsed.innerSvgXml
    + `</g>`
    + `</svg>`;
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image load failed'));
    img.src = url;
  });
}

async function rasterize(svg: string): Promise<HTMLCanvasElement | null> {
  // Data URL keeps the SVG same-origin under every browser (including
  // mobile Safari, which has historically had `<img>`+blob-URL SVG
  // quirks). `encodeURIComponent` handles the # / ( ) and other chars
  // that need escaping in a URL.
  const dataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  const img = await loadImage(dataUrl);

  const canvas = document.createElement('canvas');
  canvas.width = RASTER_SIZE;
  canvas.height = RASTER_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.clearRect(0, 0, RASTER_SIZE, RASTER_SIZE);
  ctx.drawImage(img, 0, 0, RASTER_SIZE, RASTER_SIZE);
  return canvas;
}

/**
 * Pixi-FREE rasterizer: turn an icon's lobby SVG into a 400×400
 * HTMLCanvasElement. This is everything before the Pixi `Texture.from` —
 * usable from contexts without a Pixi renderer (notably webgl3d games, whose
 * shell-3d `faceTextureForIcon` wraps the canvas in a THREE.CanvasTexture).
 * Returns null if rendering/rasterizing fails.
 */
export async function rasterizeIconToCanvas(icon: CharacterIcon): Promise<HTMLCanvasElement | null> {
  const parsed = await renderAndExtract(icon as IconValue, 100);
  if (!parsed) return null;
  const svg = buildSvgDocument(parsed);
  return rasterize(svg);
}

async function loadIconTexture(icon: CharacterIcon): Promise<Texture | null> {
  const canvas = await rasterizeIconToCanvas(icon);
  if (!canvas) return null;
  const tex = Texture.from(canvas);
  if (tex) {
    setCharacterTextureByIconHash(iconHash(icon), tex);
  }
  return tex;
}

export async function preloadMatchCharacterTextures(
  players: Array<{ icon?: CharacterIcon | undefined }>,
  _app: Application,
): Promise<void> {
  const tasks = players
    .filter((p): p is { icon: CharacterIcon } => !!p.icon)
    .map((p) => loadIconTexture(p.icon).catch((e) => {
      console.warn('[iconTexture] preload failed', e);
      return null;
    }));
  const results = await Promise.all(tasks);
  const ok = results.filter((r) => r !== null).length;
  console.log(`[iconTexture] preloaded ${ok}/${tasks.length} character textures`);
}

export function clearMatchCharacterTextures(): void {
  clearCharacterTextures();
  clear3DFaceCanvases();
}

// ── webgl3d face-canvas registry ─────────────────────────────────────────
//
// shell-3d's `faceTextureForIcon(icon)` reads a globalThis Map of
// `iconHash -> HTMLCanvasElement` (see shared/shell-3d/src/cosmetics.ts) and
// wraps the canvas in a THREE.CanvasTexture for the figure's head. The web
// bundle owns rasterization (it needs React SSR + the browser SVG renderer),
// so we write that global here. globalThis (not an import) is the shared slot
// — web does NOT depend on @tt/shell-3d, and Vite emits a separate shell-3d
// copy into each game chunk anyway, so module-scope would not be shared.
// The key + Map name MUST match cosmetics.ts exactly.
const FACE_GLOBAL = '__ttFaceCanvases';
function faceRegistry(): Map<string, HTMLCanvasElement> {
  const g = globalThis as { __ttFaceCanvases?: Map<string, HTMLCanvasElement> };
  if (!g[FACE_GLOBAL]) g[FACE_GLOBAL] = new Map<string, HTMLCanvasElement>();
  return g[FACE_GLOBAL]!;
}

function clear3DFaceCanvases(): void {
  const g = globalThis as { __ttFaceCanvases?: Map<string, HTMLCanvasElement> };
  g[FACE_GLOBAL]?.clear();
}

/**
 * Rasterize each match player's lobby avatar to a canvas and register it on
 * the global face registry that shell-3d's `faceTextureForIcon` reads. Call
 * this for webgl3d games at boot (parallel to `preloadMatchCharacterTextures`
 * for 2D games). Safe to call before the game chunk loads — registration is
 * keyed by `iconHash`, and the game reads lazily.
 */
export async function preloadMatch3DFaceCanvases(
  players: Array<{ icon?: CharacterIcon | undefined }>,
): Promise<void> {
  const reg = faceRegistry();
  const tasks = players
    .filter((p): p is { icon: CharacterIcon } => !!p.icon)
    .map(async (p) => {
      try {
        const canvas = await rasterizeIconToCanvas(p.icon);
        if (canvas) reg.set(iconHash(p.icon), canvas);
        return !!canvas;
      } catch (e) {
        console.warn('[iconTexture] 3D face rasterize failed', e);
        return false;
      }
    });
  const results = await Promise.all(tasks);
  const ok = results.filter(Boolean).length;
  console.log(`[iconTexture] preloaded ${ok}/${tasks.length} 3D face canvases`);
}
